import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { IMenuItem, IMenuItemApi } from 'types/menu';
import { MODULE_ICONS } from './constants';

export interface CourseWithModules {
  course: IMenuItem;
  modules: IMenuItem[];
}

export interface GroupedItems {
  globalModules: IMenuItem[];
  languages: {
    [language: string]: CourseWithModules[];
  };
}

export const getModuleIcon = (moduleName: string): IconDefinition => {
  const name = moduleName.toLowerCase();

  // Check if we have a specific icon for this module
  if (MODULE_ICONS[name]) {
    return MODULE_ICONS[name];
  }

  // Log error for missing icon
  console.error(`No icon found for module: "${moduleName}". Please add an icon to MODULE_ICONS in constants.ts`);

  // Return error icon instead of default
  return faCircleExclamation;
};

export const processMenuItems = (items: IMenuItemApi[]): GroupedItems => {
  const grouped: GroupedItems = {
    globalModules: [],
    languages: {},
  };

  // First pass: collect all unique languages.
  // Course-scoped items (courseId !== 0) always start with the language,
  // except legacy "Vocabulary (main)" paths where the language comes second.
  const languages = new Set<string>();
  items.forEach((item) => {
    if (item.path.length > 0 && item.courseId !== 0) {
      const topLevel = item.path[0];
      if (topLevel === 'Vocabulary (main)') {
        if (item.path.length > 1) {
          languages.add(item.path[1]);
        }
      } else {
        languages.add(topLevel);
      }
    }
  });

  items.forEach((item) => {
    if (item.path.length === 0) return;

    const topLevel = item.path[0];

    // Check if it's a global module (courseId === 0)
    if (item.courseId === 0 && item.path.length === 1) {
      const menuItem: IMenuItem = {
        ref: item.ref,
        sourceDialectId: item.sourceDialectId,
        path: item.path,
        title: topLevel,
        children: [],
        courseId: item.courseId,
      };

      // Check if we already have this module
      const exists = grouped.globalModules.some((m) => m.path[0] === topLevel);
      if (!exists && item.ref > 0) {
        grouped.globalModules.push(menuItem);
      }
    }
    // Language paths directly (e.g., ["Chinese", "Vocabulary"])
    else if (languages.has(topLevel) && item.path.length === 2) {
      if (!grouped.languages[topLevel]) {
        grouped.languages[topLevel] = [];
      }

      // This is a course directly under language
      const courseName = item.path[1];
      const existingCourse = grouped.languages[topLevel].find((c) => c.course.title === courseName);

      if (!existingCourse) {
        grouped.languages[topLevel].push({
          course: {
            ref: item.ref,
            sourceDialectId: item.sourceDialectId,
            path: item.path,
            title: courseName,
            children: [],
            courseId: item.courseId,
          },
          modules: [],
        });
      }
    }
    // Language with modules (e.g., ["English", "Sentences", "Module 1"])
    else if (languages.has(topLevel) && item.path.length >= 3) {
      if (!grouped.languages[topLevel]) {
        grouped.languages[topLevel] = [];
      }

      const courseName = item.path[1];
      let courseData = grouped.languages[topLevel].find((c) => c.course.title === courseName);

      if (!courseData) {
        // Create course entry
        courseData = {
          course: {
            ref: 0, // Will be updated if we find the course item
            sourceDialectId: item.sourceDialectId,
            path: [topLevel, courseName],
            title: courseName,
            children: [],
            courseId: item.courseId,
          },
          modules: [],
        };
        grouped.languages[topLevel].push(courseData);
      }

      // Add module
      const moduleItem: IMenuItem = {
        ref: item.ref,
        sourceDialectId: item.sourceDialectId,
        path: item.path,
        title: item.path[item.path.length - 1],
        children: [],
        courseId: item.courseId,
      };
      courseData.modules.push(moduleItem);
    }
    // Check if it's under "Vocabulary (main)"
    else if (topLevel === 'Vocabulary (main)' && item.path.length > 1) {
      const language = item.path[1];
      if (languages.has(language)) {
        if (!grouped.languages[language]) {
          grouped.languages[language] = [];
        }

        if (item.path.length === 3) {
          // This is a course under Vocabulary (main)
          const courseName = item.path[2];
          const existingCourse = grouped.languages[language].find((c) => c.course.title === courseName);

          if (!existingCourse) {
            grouped.languages[language].push({
              course: {
                ref: item.ref,
                sourceDialectId: item.sourceDialectId,
                path: item.path,
                title: courseName,
                children: [],
                courseId: item.courseId,
              },
              modules: [],
            });
          }
        } else if (item.path.length >= 4) {
          // This is a module
          const courseName = item.path[2];
          let courseData = grouped.languages[language].find((c) => c.course.title === courseName);

          if (!courseData) {
            courseData = {
              course: {
                ref: 0,
                sourceDialectId: item.sourceDialectId,
                path: [topLevel, language, item.path[2]],
                title: courseName,
                children: [],
                courseId: item.courseId,
              },
              modules: [],
            };
            grouped.languages[language].push(courseData);
          }

          const moduleItem: IMenuItem = {
            ref: item.ref,
            sourceDialectId: item.sourceDialectId,
            path: item.path,
            title: item.path[item.path.length - 1],
            children: [],
            courseId: item.courseId,
          };
          courseData.modules.push(moduleItem);
        }
      }
    }
  });

  return grouped;
};

// Generate a unique ID for a menu item based on its path and ref
export const generateMenuItemId = (item: IMenuItem | IMenuItemApi): string => {
  return `${item.path.join('-')}-${item.ref}`;
};
